import { useTimer } from '@lounge-fe/ui-kit'
import React, { useEffect, useState, createContext, useContext } from 'react'
import { useTransitionController } from '../TransitionProvider'
import type { FadeInOutContextProps } from './types'

export interface FadeInOutContextState {
  showInitialComponent: boolean
  showAnimateComponent: boolean
  setShowInitialComponent: (show: boolean) => void
  setShowAnimateComponent: (show: boolean) => void
}

const FadeInOutContext = createContext<FadeInOutContextState>(
  {} as FadeInOutContextState,
)

export const useFadeInOut = () => {
  const context = useContext(FadeInOutContext)
  if (!context) {
    throw new Error('useFadeInOut must be used within a FadeInOut provider')
  }
  return context
}

export const FadeInOutProvider: React.FC<
  React.PropsWithChildren<FadeInOutContextProps>
> = ({ delay = 5000, children }) => {
  const { isTriggered, handleTransitionEnd } = useTransitionController()
  const [showAnimateComponent, setShowAnimateComponent] = useState(false)
  const [showInitialComponent, setShowInitialComponent] = useState(true)

  const addedTimer = useTimer(() => {
    setShowAnimateComponent(false)
    handleTransitionEnd()
  }, delay)

  useEffect(() => {
    if (isTriggered) {
      setShowInitialComponent(false)
      addedTimer.start()
    }
  }, [isTriggered, addedTimer])

  const contextValue: FadeInOutContextState = {
    showInitialComponent,
    showAnimateComponent,
    setShowInitialComponent,
    setShowAnimateComponent,
  }

  return (
    <FadeInOutContext.Provider value={contextValue}>
      {children}
    </FadeInOutContext.Provider>
  )
}
